import {
  MarketTypeEnum,
  ProductTypeEnum,
  useMarketAccountsContext,
  useObservableValue,
  useSyncedRef,
  type ColumnDef,
  type Security,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { asyncScheduler, throttleTime } from 'rxjs';
import { useBalances, useFeatureFlag } from '../../../hooks';
import type { MarketSelectorRow } from '../blotter/types';
import type { MarketSelectorEnrichmentSpec, MarketSelectorItem } from '../types';
const ENRICHMENT_KEY = 'unsettled';
const UNSETTLED_BASE_COLID = 'unsettledBase';
const UNSETTLED_QUOTE_COLID = 'unsettledQuote';

type UseMarketSelectorUnsettledEnrichmentParams = {
  security: Security | undefined;
  marketSelectorItems: MarketSelectorItem[];
};

type Unsettled = {
  unsettledBase: string | undefined;
  unsettledQuote: string | undefined;
};

// Override the MarketSelectorRow type to attach our enrichment type Unsettled for typing within this hook
interface UnsettledEnrichedMarketSelectorRow extends Omit<MarketSelectorRow, 'enrichments'> {
  enrichments: { [ENRICHMENT_KEY]: Unsettled | undefined };
}
const numericColum = (id: string, field: string, currency: string | undefined) => {
  return {
    type: 'number',
    id,
    field,
    title: `Unsettled ${currency}`,
    width: 130,
    aggregate: true,
    params: {
      highlightNegative: true,
      currency,
      hideGroupRowValues: true,
    },
  } satisfies ColumnDef<any>;
};

/**
 * Builds an EnrichmentSpec object for use with the Market Selector
 *
 * Only relevant for spot symbols. If a non-spot symbol is provided, undefined will be returned.
 */
export function useUnsettledEnrichment({
  security,
  marketSelectorItems,
}: UseMarketSelectorUnsettledEnrichmentParams): MarketSelectorEnrichmentSpec<Unsettled> | undefined {
  const { canViewCreditAndBalances } = useFeatureFlag();
  // We support only Dealer markets to show Unsettled
  const availableMarketAccounts = useMemo(
    () =>
      marketSelectorItems
        .filter(item => item.type === 'MarketAccount' && item.market.Type === MarketTypeEnum.Dealer)
        .map(item => item.name),

    [marketSelectorItems]
  );

  const dataMap = useUnsettled(security, availableMarketAccounts);

  const enrichmentSpec: MarketSelectorEnrichmentSpec<Unsettled> = useMemo(
    () => {
      return {
        id: 'unsettled-enrichment',
        enrichmentKey: ENRICHMENT_KEY,
        advancedModeVisibleColIDs: [UNSETTLED_BASE_COLID],
        columns: [
          numericColum(UNSETTLED_BASE_COLID, 'enrichments.unsettled.unsettledBase', security?.BaseCurrency),
          numericColum(UNSETTLED_QUOTE_COLID, 'enrichments.unsettled.unsettledQuote', security?.QuoteCurrency),
        ] satisfies ColumnDef<UnsettledEnrichedMarketSelectorRow>[],
        dataMap,
      };
    },
    // We are rebuilding the columns on every change of quantity...
    [dataMap, security?.BaseCurrency, security?.QuoteCurrency]
  );

  const isSpot = security?.ProductType === ProductTypeEnum.Spot;
  return isSpot && canViewCreditAndBalances ? enrichmentSpec : undefined;
}

function useUnsettled(security: Security | undefined, marketAccounts: string[]): Map<string, Unsettled> {
  const { BaseCurrency, QuoteCurrency } = security || {};
  const { marketAccountsByID } = useMarketAccountsContext();
  const marketAccountsByIDRef = useSyncedRef(marketAccountsByID); // keep stable, we dont care about changes

  const requestCurrencies = useMemo(() => compact([BaseCurrency]), [BaseCurrency]);

  const { balancesByMarketAccountIDCurrencyObs } = useBalances({
    filter: {
      MarketAccounts: marketAccounts,
      Currencies: requestCurrencies,
    },
    tag: 'MarketSelector: useUnsettledEnrichment',
    throttle: '5s',
    openStreamWithoutFilter: false,
  });

  const balancesByMarketAccountIDCurrency = useObservableValue(
    () =>
      balancesByMarketAccountIDCurrencyObs.pipe(
        // i have 0 trust in the backend actually applying the throttle we send them so i do this too
        throttleTime(5000, asyncScheduler, { leading: true, trailing: true })
      ),
    [balancesByMarketAccountIDCurrencyObs]
  );

  return useMemo(() => {
    const result = new Map<string, Unsettled>();
    if (!balancesByMarketAccountIDCurrency || !BaseCurrency || !QuoteCurrency) {
      return result;
    }

    for (const [marketAccountID, balancesByCurrency] of balancesByMarketAccountIDCurrency) {
      const marketAccountName = marketAccountsByIDRef.current.get(marketAccountID)?.Name;
      if (!marketAccountName) {
        // market account doesn't exist, ignore
        continue;
      }

      const unsettledBase = balancesByCurrency.get(BaseCurrency)?.AvailableAmount;
      const unsettledQuote = balancesByCurrency.get(QuoteCurrency)?.AvailableAmount;
      result.set(marketAccountName, { unsettledBase, unsettledQuote });
    }

    return result;
  }, [balancesByMarketAccountIDCurrency, BaseCurrency, QuoteCurrency, marketAccountsByIDRef]);
}
